import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Crown, ArrowRight, Package, ShieldAlert } from "lucide-react";
import { useBilling } from "@/hooks/useBilling";
import { usePacks } from "@/hooks/usePacks";
import { registerPaymentDialogOpener } from "@/lib/axios";
import { useToast } from "@/hooks/use-toast";

export type PaymentDialogPayload = {
  featureKey: string;
  message?: string;
  used?: number | null;
  limit?: number | null;
  contactAdmin?: boolean;
  tier?: string | null;
  billingEntity?: "org";
};

type PaymentDialogContextType = {
  open: (payload: PaymentDialogPayload) => void;
};

const PaymentDialogContext = createContext<PaymentDialogContextType | null>(null);

// Small helper: map keys to human-friendly names (fallback when server message not ideal)
const FEATURE_DISPLAY: Record<string, string> = {
  active_jobs: "Active Job Postings",
  byor_uploads: "BYOR Resume Uploads",
  candidate_searches: "Candidate Searches",
  ai_interviews: "AI Interviews",
  ai_candidate_ranking: "AI Candidate Ranking",
  ai_job_generation: "AI Job Description Generation",
  ai_question_generation: "AI Interview Question Generation",
  job_applications: "Job Applications",
  applied_candidates_search: "Applied Candidates Search",

  candidate_profile_view: "Candidate Profile View",
  spotlight_intro_view: "Spotlight Intro View",
  resume_preview_view: "Resume Preview View",
  interview_video_view: "Interview Video View",
  interview_analysis_view: "Interview Analysis View",
};

const getDisplayName = (featureKey: string, message?: string) => {
  if (message && /limit reached/i.test(message)) {
    // Try to extract leading display name from "<Name> limit reached"
    const match = message.match(/^(.+?)\s+limit reached/i);
    if (match?.[1]) return match[1];
  }
  return FEATURE_DISPLAY[featureKey] || featureKey;
};


export const PaymentDialogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [visible, setVisible] = useState(false);
  const [payload, setPayload] = useState<PaymentDialogPayload | null>(null);
  const { toast } = useToast();
  const lastToastAtRef = useRef<number>(0);
  const TOAST_COOLDOWN_MS = 2000;

  const open = useCallback((p: PaymentDialogPayload) => {
    const name = getDisplayName(p.featureKey, p.message);
    const now = Date.now();
    // Debounce toast to avoid duplicates when multiple requests gate simultaneously
    if (!visible || now - lastToastAtRef.current > TOAST_COOLDOWN_MS) {
      toast({
        title: "Upgrade required",
        description: `Feature: ${name}`,
        variant: "warning",
        duration: 2500,
      });
      lastToastAtRef.current = now;
    }
    setPayload(p);
    setVisible(true);
  }, [toast, visible]);

  const close = useCallback(() => {
    setVisible(false);
    setPayload(null);
  }, []);

  useEffect(() => {
    registerPaymentDialogOpener(open);
  }, [open]);

  const ctx = useMemo(() => ({ open }), [open]);

  return (
    <PaymentDialogContext.Provider value={ctx}>
      {children}
      {visible && payload && (
        <PaymentDialogInner payload={payload} onClose={close} />
      )}
    </PaymentDialogContext.Provider>
  );
};

export const usePaymentDialog = () => {
  const ctx = useContext(PaymentDialogContext);
  if (!ctx) throw new Error("usePaymentDialog must be used within PaymentDialogProvider");
  return ctx;
};

const PaymentDialogInner: React.FC<{ payload: PaymentDialogPayload; onClose: () => void }> = ({ payload, onClose }) => {
  const { plans, upgradeSubscription } = useBilling();
  const { packs } = usePacks();

  const featureKey = payload.featureKey;
  const displayName = getDisplayName(featureKey, payload.message);

  const featurePacks = useMemo(() => packs.filter((p) => p.featureKey === featureKey), [packs, featureKey]);
  const hasPacks = featurePacks.length > 0;

  const recommendedPlan = useMemo(() => {
    // Prefer Starter; fallback to 2nd plan
    return (
      plans.find(
        (p) =>
          p.name?.toUpperCase() === "STARTER" ||
          p.name?.toUpperCase() === "RECRUIT_STARTER",
      ) ||
      plans[1] ||
      plans[0]
    );
  }, [plans]);

  const handleUpgrade = async () => {
    if (recommendedPlan) {
      try {
        await upgradeSubscription(recommendedPlan.id);
      } catch (e) {
        // swallow; button will remain
      }
    }
  };

  const handleBuyPack = () => {
    // Until pack checkout endpoint exists, deep-link to pricing
    const q = new URLSearchParams({ feature: featureKey }).toString();
    window.location.href = `/pricing?${q}`;
  };

  const leftCount = payload.limit != null && payload.used != null ? Math.max(0, payload.limit - payload.used) : null;

  const adminBlocked = payload.contactAdmin === true && payload.billingEntity === "org";

  return (
    <div className="fixed inset-0 bg-slate-950/50 flex items-center justify-center z-50 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.96 }}
        className="bg-white rounded-xl max-w-lg w-full p-6 relative"
      >
        <button onClick={onClose} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600">
          <X className="w-5 h-5" />
        </button>

        <div className="text-center">
          <div className="mx-auto mb-4 inline-flex items-center justify-center rounded-xl bg-slate-100 p-3">
            {adminBlocked ? <ShieldAlert className="w-7 h-7 text-slate-800" /> : <Crown className="w-7 h-7 text-slate-800" />}
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            {adminBlocked ? `${displayName} requires admin upgrade` : `${displayName} limit reached`}
          </h3>
          <p className="text-gray-600 mb-6">
            {payload.message || (leftCount !== null ? `You've used ${payload.used} of ${payload.limit}.` : `You've reached your current plan limit.`)}
          </p>

          {!adminBlocked && recommendedPlan && (
            <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-lg p-4 mb-5">
              <div className="flex items-center justify-center mb-1">
                <Crown className="w-4 h-4 text-slate-800 mr-2" />
                <span className="font-semibold text-slate-800">Recommended: {recommendedPlan.displayName}</span>
              </div>
              <div className="text-sm text-gray-600">
                Upgrade to unlock higher limits and premium features.
              </div>
              <div className="mt-2 text-2xl font-bold text-slate-800">{recommendedPlan.price}</div>
            </div>
          )}

          <div className="space-y-3">
            {adminBlocked ? (
              <button
                onClick={() => {
                  onClose();
                  // Could open an in-app message/email to admin; for now just close
                }}
                className="w-full bg-slate-800 text-white py-3 px-4 rounded-lg font-medium hover:bg-slate-800 transition-colors"
              >
                Contact your admin
              </button>
            ) : (
              <button
                onClick={handleUpgrade}
                className="w-full bg-slate-800 text-white py-3 px-4 rounded-lg font-medium hover:bg-slate-800 transition-colors flex items-center justify-center"
              >
                Upgrade Plan
                <ArrowRight className="w-4 h-4 ml-2" />
              </button>
            )}

            {!adminBlocked && hasPacks && (
              <button
                onClick={handleBuyPack}
                className="w-full text-slate-950 py-2 px-4 rounded-lg font-medium hover:bg-slate-50 transition-colors flex items-center justify-center"
              >
                <Package className="w-4 h-4 mr-2" /> Buy a Feature Pack
              </button>
            )}

            {!adminBlocked && !hasPacks && (
              <button
                onClick={() => (window.location.href = "/pricing")}
                className="w-full text-slate-950 py-2 px-4 rounded-lg font-medium hover:bg-slate-50 transition-colors"
              >
                View all plans
              </button>
            )}
          </div>

          <p className="text-xs text-gray-500 mt-4">Upgrade anytime. Cancel anytime. No long-term commitments.</p>
        </div>
      </motion.div>
    </div>
  );
};
